import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { Paginated, PaginationQuery } from "../../types";
import useApi, { buildUrl } from "../api";
import { CustomError } from "../errors";
import { Priority, UpsertPriority } from "./types";

const usePriorityApi = () => {
  const baseurl = "/priorities";

  const api = useApi();

  const searchPriorities = useCallback(
    async (query: PaginationQuery, signal?: AbortSignal) => {
      const url = buildUrl(baseurl, query);
      const data = await api.get<Paginated<Priority>>({ url, signal });
      return data.items;
    },
    [api]
  );

  const usePriorities = useCallback(
    (query: PaginationQuery) => {
      const url = buildUrl(baseurl, query);
      const result = useQuery<Paginated<Priority>, CustomError>({
        queryKey: [url],
        queryFn: ({ signal }) => api.get<Paginated<Priority>>({ url, signal }),
      });
      return { ...result, error: result.error ?? undefined };
    },
    [api]
  );

  const createPriority = useCallback(
    async (p: UpsertPriority) => {
      const data = await api.post<UpsertPriority, string>(baseurl, p);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  const updatePriority = useCallback(
    async (p: UpsertPriority, priorityID: string) => {
      const data = await api.put<UpsertPriority>(`${baseurl}/${priorityID}`, p);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  const deletePriority = useCallback(
    async (priorityID: string) => {
      const data = await api.del(`${baseurl}/${priorityID}`);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  return {
    searchPriorities,
    usePriorities,
    createPriority,
    updatePriority,
    deletePriority,
  };
};

export default usePriorityApi;
